import { useCallback } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Grid,
    CircularProgress
} from '@material-ui/core';
import { Formik, Form } from 'formik';
import FormikInput from '../atom/FormikInput';
import { doSwaggerCall } from '../../hooks/useSwaggerClient';
import { useNotify } from '../../hooks/useNotify';

const CreateOrEditPriceItemNameModal = ({
    showModal,
    MODAL_TYPE,
    priceItem = null,
    reloadPriceItemNames
}) => {
    const { notifySuccess, notifyError } = useNotify();

    const createPriceItemName = useCallback(
        async (values, { setSubmitting, setErrors }) => {
            try {
                await doSwaggerCall('priceitemnames', 'createPriceItemName', {
                    newPriceItemName: {
                        name: values.name
                    }
                });
                notifySuccess('New price item name created!');
                showModal(MODAL_TYPE.NONE);
                reloadPriceItemNames();
            } catch (err) {
                console.error(err);
                if (err.status === 409) {
                    setErrors({ name: 'This name already exists. Please choose another.' });
                } else {
                    setErrors({ api: 'Creation failed. Please try again.' });
                }
                notifyError('Error during price item name creation!');
            } finally {
                setSubmitting(false);
            }
        },
        [notifySuccess, notifyError, showModal, MODAL_TYPE, reloadPriceItemNames]
    );

    const updatePriceItemName = useCallback(
        async (values, { setSubmitting, setErrors }) => {
            try {
                await doSwaggerCall('priceitemnames', 'updatePriceItemName', {
                    priceItemNameId: priceItem.id,
                    priceItemName: {
                        name: values.name
                    }
                });
                notifySuccess('Price item name updated!');
                showModal(MODAL_TYPE.NONE);
                reloadPriceItemNames();
            } catch (err) {
                console.error(err);
                if (err.status === 409) {
                    setErrors({ name: 'This name already exists. Please choose another.' });
                } else {
                    setErrors({ api: 'Update failed. Please try again.' });
                }
                notifyError('Error during price item name update!');
            } finally {
                setSubmitting(false);
            }
        },
        [priceItem, notifySuccess, notifyError, showModal, MODAL_TYPE, reloadPriceItemNames]
    );

    return (
        <Dialog
            aria-labelledby="client-modal"
            open
            onClose={() => showModal(MODAL_TYPE.NONE)}
            maxWidth="md"
        >
            <DialogTitle>
                {priceItem ? 'Edit price item name' : 'Create new price item name'}
            </DialogTitle>

            <Formik
                initialValues={{ name: priceItem?.name || '' }}
                validate={values => {
                    const errors = {};
                    if (!values.name?.trim()) {
                        errors.name = 'Name is required';
                    }
                    return errors;
                }}
                onSubmit={(values, formikHelpers) =>
                    priceItem
                        ? updatePriceItemName(values, formikHelpers)
                        : createPriceItemName(values, formikHelpers)
                }
            >
                {({ isSubmitting, isValid, dirty, errors }) => (
                    <Form id="neworeditpriceitemname-form">
                        <DialogContent>
                            <Grid container spacing={2}>
                                {errors?.api && (
                                    <Grid item xs={12} style={{ color: '#d32f2f' }}>
                                        {errors.api}
                                    </Grid>
                                )}
                                <Grid item xs={12}>
                                    <FormikInput
                                        label="Name"
                                        required
                                        type="text"
                                        name="name"
                                        sm={12}
                                    />
                                </Grid>
                            </Grid>
                        </DialogContent>
                        <DialogActions>
                            {isSubmitting && <CircularProgress size={24} />}
                            <Button variant="contained" onClick={() => showModal(MODAL_TYPE.NONE)}>
                                Close
                            </Button>
                            <Button
                                variant="contained"
                                color="primary"
                                type="submit"
                                disabled={isSubmitting || !(isValid && dirty)}
                            >
                                Submit
                            </Button>
                        </DialogActions>
                    </Form>
                )}
            </Formik>
        </Dialog>
    );
};

export default CreateOrEditPriceItemNameModal;
